import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { Icon } from './ui/Icon.js';

export interface TabItem<T extends string> {
  id: T;
  label: string;
  /** Boxicon name (e.g. 'bx-file'), not a component. Spec 230. */
  icon?: string;
  testId?: string;
}

interface TabsProps<T extends string> {
  tabs: readonly TabItem<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  ariaLabel?: string;
}

function slugify(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '');
}

/**
 * The id a `role="tabpanel"` for `tabId` must carry (as `id`, with
 * `aria-labelledby` pointing at the matching tab id below) for a caller that
 * wires its own panel. Exported so the two ids can never drift apart.
 */
export function tabPanelId(ariaLabel: string, tabId: string): string {
  return `${slugify(ariaLabel)}-panel-${tabId}`;
}

function tabId(ariaLabel: string, id: string): string {
  return `${slugify(ariaLabel)}-tab-${id}`;
}

/**
 * Spec 230 — Kridt's tabs. Full ARIA tablist pattern: `tablist`/`tab` roles,
 * roving tabindex, arrow keys, Home/End, automatic activation (moving focus
 * changes the selected tab, it doesn't just move a highlight). The indicator
 * is a 2px ink underline sitting on the tablist's own hairline.
 *
 * Ruling (spec 230 Task 4): this component only renders the tablist. Neither
 * of today's two call sites (`PromptsPage`, `SettingsPage`) wraps its content
 * in a matching `role="tabpanel"` element, and wiring that up touches page
 * layout outside this task's declared file list (`Tabs.tsx` only) — the same
 * out-of-scope boundary spec 230 already drew around `icon-map.md` in
 * c2c1357. `tabPanelId` is exported so a future page-layout spec (231–234)
 * can add the panel wrapper without inventing a second id scheme.
 */
export function Tabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel = 'Tabs',
}: TabsProps<T>) {
  const tabRefs = useRef<Map<T, HTMLButtonElement>>(new Map());

  const activate = (tab: TabItem<T>) => {
    onTabChange(tab.id);
    tabRefs.current.get(tab.id)?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    activate(tabs[nextIndex]);
  };

  return (
    <div className="border-b border-[var(--hub-color-line)]">
      <div role="tablist" aria-label={ariaLabel} className="-mb-px flex gap-6">
        {tabs.map((tab, index) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) tabRefs.current.set(tab.id, el);
                else tabRefs.current.delete(tab.id);
              }}
              id={tabId(ariaLabel, tab.id)}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={tabPanelId(ariaLabel, tab.id)}
              tabIndex={selected ? 0 : -1}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-testid={tab.testId}
              className={`inline-flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                selected
                  ? 'border-[var(--hub-tab-indicator)] text-[var(--hub-tab-ink-selected)]'
                  : 'border-transparent text-[var(--hub-tab-ink)] hover:text-[var(--hub-color-ink)]'
              }`}
            >
              {tab.icon && <Icon name={tab.icon} />}
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
